import React, { useState, useRef, useEffect } from "react";
import { num, uid, requestFullAccess } from "../lib/utils";
import { getMileageAdjustedValue, resolveValue, resolveBestOffer } from "../lib/valuation";
import { solveLoan } from "../lib/loanMath";
import { CT, STARTERS, callClyde, calcSavings } from "../lib/clydeAssistant";

// Used only if the live FRED rate hasn't been fetched anywhere in reach
// of this component (Kate has no rate fetch of her own — this box is a
// rough, pre-worksheet estimate, not the real financing math the
// worksheet itself does once Clyde's confirmed and BuyingModal mounts).
const FALLBACK_BASE_RATE=7.5;

// Kate's own guided-intake wizard — trade/down payment/budget/new-used/
// YMM, the on-ramp she now owns (moved here from HomeFleet_BuyingFlow.jsx's
// ClydeChat, which now only asks about trim, Clyde's own remaining
// deep-dive after a confirmed handoff). Deliberately opt-in, not the
// default entry state — unlike the old buying-only ClydeChat, Kate
// handles general household questions too, so forcing every conversation
// through "are you trading in a vehicle?" would be wrong; only the
// STARTERS "I want to buy another vehicle" button starts it (see
// wizardStep's own null-means-not-started initial state below). Same
// mechanics as the original: button steps for trade/new-used, client-side
// number parsing for down/budget (no network call for a plain dollar
// figure), YMM routed through a real Claude turn since it needs genuine
// vehicle-name understanding. Typing free text at a button step, or a
// non-numeric answer at down/budget, exits the wizard and falls straight
// through to Kate's own normal free-form conversation — same escape
// hatch, same reasoning.
const WIZARD_STEPS=[
  {key:"trade",prompt:"First — are you trading in a vehicle, or just adding one?"},
  // A function rather than a string, so this step can say what Kate
  // already knows. Everything it needs is resolvable the moment the trade
  // step is answered -- the same resolveValue/resolveBestOffer calls
  // formatHandoffSummary already makes below. Asking someone to plan a
  // down payment without mentioning the equity on their own dashboard is
  // not technically asking twice, but it reads that way.
  {key:"down",prompt:function(ctx){
    const a=ctx.wizardAnswers||{};
    if(!a.tradeVehicleNickname)return "How much are you planning to put down?";
    const v=(ctx.vehicles||[]).find(function(x){return x.nickname.toLowerCase()===String(a.tradeVehicleNickname).toLowerCase();});
    const rv=v?resolveValue(v):null;
    const bo=(v&&rv&&rv.amount!=null)?resolveBestOffer(v,rv.amount):null;
    // No resolvable value means the plain question. Never invent, never
    // half-fill -- a wrong equity figure here is worse than no figure.
    if(!v||!bo||bo.amount==null)return "How much are you planning to put down?";
    const payoff=Number(v.loan)||0;
    const equity=Math.round(bo.amount-payoff);
    // resolveBestOffer reports its own source; an estimate is hedged as
    // one rather than stated as a firm number, same rule as every other
    // figure in this app.
    const hedge=bo.source==="estimated"?"looks like about ":"";
    if(equity<=0)return "Your "+a.tradeVehicleNickname+" "+(hedge?"looks like it is":"is")+" carrying more loan than it is worth right now, so there is no equity coming off this deal. How much cash are you planning to put down?";
    return "Your "+a.tradeVehicleNickname+" "+hedge+"$"+equity.toLocaleString()+" of equity after the $"+payoff.toLocaleString()+" payoff, and that comes off this deal. How much cash are you planning to put down on top of that?";
  }},
  {key:"budget",prompt:"What's your target — a monthly payment or a total price ceiling? (e.g. \"$650/mo\" or \"$45,000 total\")"},
  {key:"newUsed",prompt:"New or pre-owned?"},
  {key:"ymm",prompt:"What are you looking for — year, make, and model?"},
];

function promptText(step,ctx){
  return typeof step.prompt==="function"?step.prompt(ctx):step.prompt;
}
// Built here so the question is appended to msgs at the same moment its
// answer is, which is what guarantees a question always precedes an
// answer rather than merely usually preceding one.
function wizardQuestionMessage(step,ctx){
  return{role:"assistant",kind:"wizard-question",stepKey:step.key,content:promptText(step,ctx)};
}

function parseDollarAmount(text){
  const m=String(text).replace(/,/g,"").match(/\$?\s*(\d+(?:\.\d+)?)\s*(k)?/i);
  if(!m)return null;
  let n=parseFloat(m[1]);
  if(m[2])n*=1000;
  return Number.isFinite(n)&&n>0?Math.round(n):null;
}

// Distilled handoff summary for Kate -> Clyde — built from Kate's own
// accumulated extract_deal_intent captures, never from her free-form
// reply text. The trade figure specifically is resolved here via the
// real resolveBestOffer (same fix as the earlier trade-value hallucination
// pass), never taken from anything the model said — extract_deal_intent
// only ever captures the trade vehicle's NICKNAME, never a dollar amount.
// Appended onto Kate's own transition line in send() below so the whole
// thing reads as one message, matching the locked design ("visible as
// part of Kate's own handoff message, not silently injected") while
// guaranteeing every number in it is real.
function formatHandoffSummary(intent,vehicles){
  if(!intent)return "";
  const parts=[];
  if(intent.year||intent.make||intent.model)parts.push([intent.year,intent.make,intent.model,intent.trim].filter(Boolean).join(" "));
  if(intent.tradeVehicleNickname){
    const v=vehicles.find(function(x){return x.nickname.toLowerCase()===intent.tradeVehicleNickname.toLowerCase();});
    const resolvedValue=v?resolveValue(v):null;
    const bo=(v&&resolvedValue&&resolvedValue.amount!=null)?resolveBestOffer(v,resolvedValue.amount):null;
    parts.push("trading your "+intent.tradeVehicleNickname+(bo?" ($"+bo.amount.toLocaleString()+" offer)":""));
  }
  if(intent.downPayment)parts.push("$"+intent.downPayment.toLocaleString()+" down");
  if(intent.budget)parts.push("$"+intent.budget.toLocaleString()+(intent.budgetType==="monthly"?"/mo budget":" budget"));
  return parts.join(", ");
}

// open/onOpenChange: lifted to HomeFleetApp.jsx (a controlled pair, same
// shape as every other lifted-state prop this session) so the drawer can
// be reopened from OUTSIDE this component too — specifically, the small
// "General chat" icon HomeFleet_BuyingFlow.jsx now renders while the Buy
// Center is open. hideTrigger suppresses only the floating "Ask Clyde"
// button (never the drawer itself, which still renders whenever open is
// true) — HomeFleetApp.jsx sets it while activeView==="buying", since
// that view has its own reopen icon and the floating button would
// otherwise sit on top of it. Nothing about this component's own
// internal state (msgs, conversationId, dealId, ...) changes just
// because the trigger is hidden or the drawer is reopened from
// elsewhere — it was never unmounted, so nothing is ever lost.
// Lifted out of the render so msgs can be seeded with it. The greeting
// used to render from its own block gated on msgs.length===0, which meant
// it unmounted the instant the first message was appended -- the household
// watched the conversation's opening line disappear on their first click.
// It is a message; it now lives where messages live.
function greetingMessage(vehicles){
  const ts=calcSavings(vehicles);
  const vLabel=vehicles.length>1?vehicles.length+" vehicles":(vehicles[0]&&vehicles[0].nickname)||"vehicle";
  const greetSavings=ts>0?" and I've spotted ~$"+ts.toLocaleString()+"/mo in potential savings":"";
  return{role:"assistant",kind:"greeting",content:"Hi, I'm Kate — your household concierge.\n\nI can see your "+vLabel+greetSavings+".\n\nWhat can I help with?"};
}
// Anything beyond the greeting. The old "New chat" gate was msgs.length>0,
// which with a seeded greeting would be true from mount -- offering to
// reset a conversation that has not started.
function hasRealConversation(msgs){
  return (msgs||[]).some(function(m){return (m.kind||"chat")!=="greeting";});
}

export function ClydePanel({vehicles,mode,onSchedule,onBuying,liveValues,accessLevel,open,onOpenChange,hideTrigger}){
  const[msgs,setMsgs]=useState(function(){return[greetingMessage(vehicles)];});
  const[inp,setInp]=useState("");
  const[busy,setBusy]=useState(false);
  const[starters,setStarters]=useState(true);
  const[err,setErr]=useState("");
  const[capNote,setCapNote]=useState(false); // true only on demo_chat_cap — a real daily limit, not a failure, gets its own nudge instead of the generic err banner
  const[goLiveSent,setGoLiveSent]=useState(false);
  // Which persisted thread (migration 0023's conversations table) the
  // current msgs belong to — null until the first message of a fresh
  // thread is sent, at which point send() mints one via uid() (same
  // client-generated-id pattern VehicleModal.jsx already uses) and
  // reuses it for every later turn in this same open conversation.
  const[conversationId,setConversationId]=useState(null);
  // Transient, one-turn-only — not persisted anywhere itself. Always
  // false in this pass (nothing yet sets it true: loading a past
  // conversation back into msgs is Pass 2's History UI, not built here)
  // but the mechanism is real: whichever future code path rehydrates
  // msgs from a past thread should setJustResumed(true) right after, and
  // send() clears it again the moment that one turn completes, so nudge
  // language ("this may be stale") only ever appears once per resume,
  // not on every later message in that same active session.
  const[justResumed,setJustResumed]=useState(false);
  // Accumulates whatever Kate's extract_deal_intent tool has captured so
  // far this conversation (functions/api/chat.js's own tool-use loop,
  // read off callClyde's now-object return — see clydeAssistant.js).
  // Merged, never replaced wholesale, so a later turn's partial capture
  // adds to/overwrites individual fields rather than wiping out what an
  // earlier turn already established. Handed to onBuying as the real
  // handoff payload — Clyde's own conversation recaps and confirms it
  // before doing anything with it (see formatHandoffSummary below and
  // HomeFleet_BuyingFlow.jsx's own client-side confirmation gate), never
  // seeds the worksheet directly anymore.
  const[extractedIntent,setExtractedIntent]=useState(null);
  // The linked deals row's id, once extract_deal_intent has captured
  // enough for chat.js to mint one. Tracked and resent on every later
  // turn (same pattern HomeFleet_BuyingFlow.jsx's ClydeChat already
  // uses for its own dealId) so a later turn's capture updates that same
  // row instead of chat.js minting a new one each time — without this,
  // every new-intent turn in this chat would silently spawn a duplicate
  // deals row rather than staying linked to one.
  const[dealId,setDealId]=useState(null);
  // The household's active (draft/sent) deal, if any — fetched once per
  // drawer open from functions/api/deals.js's GET /api/deals, so Kate can
  // reference that a handoff to Clyde happened even in a conversation
  // that never itself made one (see clydeAssistant.js's buildKateSystem/
  // formatHandoffStatus — status only, never the deal's own specifics).
  // Skipped for demo accounts since the buying flow — and so any deal —
  // is unreachable there.
  const[activeDeal,setActiveDeal]=useState(null);
  // Kate's own wizard state (see WIZARD_STEPS above). null means "not
  // started" — the default, since Kate handles general questions too and
  // shouldn't force every conversation through a buying intake. Only the
  // STARTERS "I want to buy another vehicle" button sets this to 0.
  // wizardAnswers mirrors what's been captured so far, independent of
  // extractedIntent's own accumulation, purely so the rough-estimate box
  // (right after YMM, per the locked design) has immediate access to
  // budget/downPayment/make/model the instant they're answered.
  const[wizardStep,setWizardStep]=useState(null);
  const[wizardAnswers,setWizardAnswers]=useState({});
  const wizardActive=wizardStep!==null&&wizardStep<WIZARD_STEPS.length;
  // One-shot visual cue for the handoff moment (locked design: "Kate's
  // avatar/name giving way to Clyde's", not a silent surface swap) — set
  // true right when HANDOFF_TO_CLYDE fires, shown for the same window
  // the existing transition delay already gives the household to read
  // Kate's message, then the drawer actually closes and navigation
  // happens exactly as before. Reset on reset()/close so a later,
  // unrelated open never opens already mid-transition.
  const[handoffTransition,setHandoffTransition]=useState(false);
  const bot=useRef();
  const inpRef=useRef();

  useEffect(()=>{if(open)setTimeout(()=>inpRef.current&&inpRef.current.focus(),300);},[open]);
  useEffect(()=>{
    if(!open||accessLevel==="demo")return;
    let cancelled=false;
    fetch("/api/deals").then(r=>r.ok?r.json():null).then(d=>{if(!cancelled&&d)setActiveDeal(d.activeDeal||null);}).catch(()=>{});
    return ()=>{cancelled=true;};
  },[open,accessLevel]);
  useEffect(()=>{if(bot.current)bot.current.scrollIntoView({behavior:"smooth"});},[msgs,busy]);

  const reset=()=>{setMsgs([greetingMessage(vehicles)]);setStarters(true);setInp("");setErr("");setCapNote(false);setConversationId(null);setJustResumed(false);setExtractedIntent(null);setDealId(null);setWizardStep(null);setWizardAnswers({});setHandoffTransition(false);};
  const close=()=>{setHandoffTransition(false);onOpenChange(false);};

  // Records one wizard answer as a visible user-role bubble, merges the
  // patch into extractedIntent (via onIntentUpdate-equivalent — Kate has
  // no parent to forward to, extractedIntent IS her own running record)
  // and the local wizardAnswers mirror, and advances to the next step.
  // patch may be null (e.g. "no trade") — still a real answer worth
  // advancing past.
  function answerWizardStep(patch,userLabel){
    const mergedAnswers={...wizardAnswers,...(patch||{})};
    if(patch){
      setExtractedIntent(function(p){return{...(p||{}),...patch};});
      setWizardAnswers(mergedAnswers);
    }
    // The answer and the NEXT question are appended in one update, which
    // is what makes "a question always precedes its answer" structural
    // rather than a convention someone has to remember. Two adjacent user
    // turns would mean that invariant slipped; the API tolerates it, so it
    // would fail silently.
    //
    // mergedAnswers, not wizardAnswers: the next prompt may need the
    // answer just given -- the down step names the equity of the vehicle
    // chosen one step earlier -- and setWizardAnswers has not applied yet.
    const nextStep=WIZARD_STEPS[wizardStep+1];
    setMsgs(function(p){
      const out=p.concat([{role:"user",kind:"wizard-answer",content:userLabel}]);
      return nextStep?out.concat([wizardQuestionMessage(nextStep,{wizardAnswers:mergedAnswers,vehicles:vehicles})]):out;
    });
    setWizardStep(function(s){return s+1;});
  }
  // Starting the wizard appends the starter's own label as a real user
  // turn. It is what the household actually clicked, it makes the
  // transcript honest, and structurally it is what keeps the API payload
  // user-first once wizard questions are included -- the first question is
  // an assistant turn, so without this anchor the filtered payload would
  // begin with one. See HOMEFLEET_WIZARD_MESSAGING_PLAN.md section 4.
  function startWizard(starterLabel){
    setStarters(false);
    setMsgs(function(p){return p.concat([
      {role:"user",kind:"wizard-answer",content:starterLabel},
      wizardQuestionMessage(WIZARD_STEPS[0],{wizardAnswers:{},vehicles:vehicles}),
    ]);});
    setWizardStep(0);
  }
  function exitWizard(){setWizardStep(WIZARD_STEPS.length);}

  const SVC_INTENT=/\b(schedule|book|set up|make)\b.{0,40}\b(oil change|service|maintenance|tire rotation|rotation|brake service|appointment)\b|\b(oil change|tire rotation|brake service)\b.{0,30}\b(schedule|book|appointment)\b/i;
  const send=async(text)=>{
    const t=(text||inp).trim();
    if(!t||busy)return;
    // Wizard text-type steps ("down"/"budget") are parsed client-side —
    // no network call for a plain dollar figure. "trade"/"newUsed" are
    // button-only — reaching send() at all during one of those means the
    // composer was used instead of a button, which always means escape.
    // "ymm" is deliberately NOT handled here — it falls straight through
    // to the real API call below, with its own advance-or-stay logic
    // after the response. Same mechanics as HomeFleet_BuyingFlow.jsx's
    // own wizard (now trim-only), which this was ported from.
    if(wizardActive){
      const step=WIZARD_STEPS[wizardStep];
      if(step&&(step.key==="trade"||step.key==="newUsed")){
        exitWizard();
      }else if(step&&step.key==="down"){
        const amt=parseDollarAmount(t);
        if(amt!=null){setInp("");answerWizardStep({downPayment:amt},t);return;}
        exitWizard();
      }else if(step&&step.key==="budget"){
        const amt=parseDollarAmount(t);
        if(amt!=null){
          const isMonthly=/\/\s*mo\b|\bmonth/i.test(t);
          setInp("");
          answerWizardStep({budget:amt,budgetType:isMonthly?"monthly":"total_price"},t);
          return;
        }
        exitWizard();
      }
    }
    setInp("");setStarters(false);setErr("");setCapNote(false);
    const next=[...msgs,{role:"user",content:t}];
    setMsgs(next);
    if(SVC_INTENT.test(t)&&onSchedule){
      setMsgs(function(p){return p.concat([{role:"assistant",content:"On it — opening the Service Center so we can get this scheduled."}]);});
      setTimeout(function(){onSchedule();},600);
      return;
    }
    setBusy(true);
    const isNewConversation=!conversationId;
    const cid=conversationId||uid();
    if(isNewConversation)setConversationId(cid);
    try{
      // callClyde now resolves to {text,extractedIntent,dealId}, not a
      // bare string (see clydeAssistant.js's own comment on the change).
      const clydeRep=await callClyde(next,vehicles,mode,liveValues,accessLevel,cid,"Kate",isNewConversation,justResumed,dealId,activeDeal);
      let rep=clydeRep.text;
      if(justResumed)setJustResumed(false); // one-turn-only hint, consumed
      if(clydeRep.dealId&&clydeRep.dealId!==dealId)setDealId(clydeRep.dealId);
      // Merged locally, not read back from state, deliberately — setState
      // is async, so reading the `extractedIntent` closure variable right
      // after calling setExtractedIntent below would still see the OLD
      // value if this same turn also needs to hand intent to onBuying
      // (the OPEN_BUYING branch just below). mergedIntent is the single
      // source of truth for both the state update and this turn's own use.
      const mergedIntent=clydeRep.extractedIntent?{...extractedIntent,...clydeRep.extractedIntent}:extractedIntent;
      if(clydeRep.extractedIntent){
        setExtractedIntent(mergedIntent);
        setWizardAnswers(function(p){return{...p,...clydeRep.extractedIntent};});
      }
      // "ymm" is the only wizard step routed through a real Claude turn
      // (unlike "down"/"budget" above) since it needs genuine vehicle-name
      // understanding — extract_deal_intent already does this reliably.
      // Auto-advance only when THIS turn's own extraction actually
      // captured make+model; if not, stay on the step — Kate's own reply
      // naturally re-asks. Once ymm completes, the wizard is simply done
      // (see wizardActive) — Kate falls through to normal free-form
      // conversation, and her own already-tuned BUYING FLOW HANDOFF
      // instruction decides when to actually emit HANDOFF_TO_CLYDE, no
      // separate hardcoded trigger needed here.
      if(wizardActive){
        const step=WIZARD_STEPS[wizardStep];
        if(step&&step.key==="ymm"&&clydeRep.extractedIntent&&clydeRep.extractedIntent.make&&clydeRep.extractedIntent.model){
          setWizardStep(function(s){return s+1;});
        }
      }
      if(rep.indexOf("OPEN_SCHEDULER")>=0){
        rep=rep.replace(/\s*OPEN_SCHEDULER\s*/g," ").trim()||"Let's get this scheduled — opening the Service Center.";
        setMsgs(function(p){return p.concat([{role:"assistant",content:rep}]);});
        setTimeout(function(){onSchedule&&onSchedule();},800);
        setBusy(false);
        return;
      }
      // Same sentinel-token pattern as OPEN_SCHEDULER above, but the
      // payload is now a real handoff, not a worksheet seed — onBuying
      // starts Clyde's own separate conversation (HomeFleetApp.jsx),
      // which holds off on any research until the household explicitly
      // confirms this summary is accurate (functions/api/chat.js's own
      // HANDOFF FROM KATE instruction + HomeFleet_BuyingFlow.jsx's client
      // gate). The distilled summary is built here, deterministically,
      // from mergedIntent (Kate's own accumulated extract_deal_intent
      // captures) — never from Kate's own reply text — and appended onto
      // her transition line so the whole thing reads as one message, per
      // the locked design: visible as part of Kate's own handoff, not a
      // silent injection.
      if(rep.indexOf("HANDOFF_TO_CLYDE")>=0){
        rep=rep.replace(/\s*HANDOFF_TO_CLYDE\s*/g," ").trim()||"Let me bring in Clyde — he'll take it from here.";
        const summary=formatHandoffSummary(mergedIntent,vehicles);
        if(summary)rep=rep+"\n\nI'm passing along: "+summary+".";
        setMsgs(function(p){return p.concat([{role:"assistant",content:rep}]);});
        // Narrated handoff, not a silent surface swap: Kate's own header
        // visibly gives way to Clyde's (see handoffTransition below) for
        // this same window the household already had to read her message
        // in, THEN the drawer actually closes and navigation happens —
        // previously the conversational path (unlike the manual "Open
        // Deal Worksheet" button) never closed the drawer at all, leaving
        // it sitting open on top of the newly-loaded Buy Center.
        setHandoffTransition(true);
        setTimeout(function(){close();onBuying&&onBuying(mergedIntent);},800);
        setBusy(false);
        return;
      }
      setMsgs(function(p){return p.concat([{role:"assistant",content:rep}]);});
    }catch(e){
      if(e.reason==="demo_chat_cap"){
        setCapNote(true);
        setMsgs(function(p){return p.concat([{role:"assistant",content:"I've hit today's chat limit on this demo account — I'll be back to full speed tomorrow. A live account gets a lot more room to talk things through."}]);});
      }else{
        setErr(e.message||"Connection error — try again.");
        setMsgs(function(p){return p.concat([{role:"assistant",content:"I had trouble with that — please try again."}]);});
      }
    }
    setBusy(false);
  };

  const isLive=mode==="live";

  // This drawer is Kate's own home, always — handoffTransition is the one
  // brief exception, previewing Clyde's identity for the same window the
  // household has to read Kate's handoff message before the drawer
  // actually closes and navigation happens (see send()'s own
  // HANDOFF_TO_CLYDE branch). Not a dual-persona component the rest of
  // the time — just this one narrated moment.
  const persona=handoffTransition
    ?{name:"Clyde",icon:"🔑",subtitle:"Vehicle Purchase Specialist",accent:CT.sky}
    :{name:"Kate",icon:"🏡",subtitle:"Your Household Concierge",accent:CT.kate};

  function Dot(props){
    return React.createElement("div",{style:{width:7,height:7,borderRadius:"50%",background:persona.accent,animation:"clydeTyping 1.2s ease-in-out "+(props.i*0.2)+"s infinite"}});
  }
  function Av(){
    return React.createElement("div",{style:{width:30,height:30,borderRadius:"50%",background:"linear-gradient(135deg,"+persona.accent+","+CT.navyMid+")",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0,boxShadow:"0 0 0 2px "+CT.navyLt}},persona.icon);
  }

  return(
    <React.Fragment>
      {!hideTrigger&&(
        <button onClick={()=>onOpenChange(true)} style={{position:"fixed",bottom:28,right:28,background:"linear-gradient(135deg,"+CT.kate+","+CT.navyMid+")",border:"none",borderRadius:50,padding:"13px 22px",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:9,animation:"clydePulse 2.8s ease-in-out infinite",zIndex:888,boxShadow:"0 4px 20px rgba(45,212,191,0.4)"}}>
          <span style={{fontSize:18}}>🏡</span>
          <span>Ask Kate</span>
        </button>
      )}

      {open&&<div onClick={close} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:889,backdropFilter:"blur(3px)"}}/>}

      {open&&(
        <div style={{position:"fixed",top:0,right:0,bottom:0,width:"min(420px, 100vw)",background:"linear-gradient(180deg,"+CT.navy+" 0%,"+CT.navyMid+" 100%)",boxShadow:"-8px 0 40px rgba(0,0,0,0.5)",zIndex:890,display:"flex",flexDirection:"column",animation:"clydeIn 0.28s cubic-bezier(0.34,1.05,0.64,1)",borderLeft:"1px solid "+CT.border}}>

          <div style={{padding:"16px 18px",borderBottom:"1px solid "+CT.border,display:"flex",alignItems:"center",gap:12,flexShrink:0,transition:"background .3s"}}>
            <div style={{width:44,height:44,borderRadius:"50%",background:"linear-gradient(135deg,"+persona.accent+","+CT.navyMid+")",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,boxShadow:"0 0 0 2px "+CT.navyLt+",0 0 16px "+persona.accent+"4d",transition:"background .3s,box-shadow .3s"}}>{persona.icon}</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:800,fontSize:17,color:"#fff"}}>{persona.name}</div>
              <div style={{fontSize:11,color:persona.accent,marginTop:1,fontWeight:500,display:"flex",alignItems:"center",gap:6}}>
                <span>{persona.subtitle}</span>
                <span style={{fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:4,background:isLive?"rgba(22,163,74,0.2)":"rgba(180,83,9,0.2)",color:isLive?"#4ade80":"#fcd34d",border:"1px solid "+(isLive?"rgba(22,163,74,0.3)":"rgba(180,83,9,0.3)")}}>
                  {isLive?"LIVE":"DEMO"}
                </span>
              </div>
            </div>
            <div style={{display:"flex",gap:6}}>
              {hasRealConversation(msgs)&&<button onClick={reset} style={{background:"none",border:"none",color:CT.muted,fontSize:12,cursor:"pointer",padding:"4px 8px",fontWeight:600}}>New chat</button>}
              <button onClick={close} style={{background:"none",border:"1px solid "+CT.border,borderRadius:8,width:32,height:32,color:CT.muted,cursor:"pointer",fontSize:15,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            </div>
          </div>

          <div style={{padding:"8px 18px",borderBottom:"1px solid "+CT.border,display:"flex",gap:14,overflowX:"auto",flexShrink:0}}>
            {vehicles.map(function(v){
              var adj=getMileageAdjustedValue(v),eq=adj-num(v.loan);
              return(
                <div key={v.id} style={{flexShrink:0,display:"flex",alignItems:"center",gap:7}}>
                  <span style={{fontSize:15}}>🚗</span>
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:CT.offWhite,whiteSpace:"nowrap"}}>{v.nickname}</div>
                    <div style={{fontSize:10,color:eq>=0?persona.accent:CT.muted,fontWeight:600,whiteSpace:"nowrap"}}>{"$"+eq.toLocaleString()}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{padding:"10px 18px",borderBottom:"1px solid "+CT.border,flexShrink:0}}>
            <button
              onClick={()=>{if(accessLevel!=="demo"){close();onBuying&&onBuying(extractedIntent);}}}
              disabled={accessLevel==="demo"}
              title={accessLevel==="demo"?"Live account required — go live to build a deal":"Open the deal worksheet"}
              style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,background:accessLevel==="demo"?"rgba(255,255,255,0.03)":"linear-gradient(135deg,"+CT.sky+","+CT.navyMid+")",border:"1px solid "+(accessLevel==="demo"?CT.border:"transparent"),borderRadius:10,padding:"9px 12px",color:accessLevel==="demo"?CT.muted:"#fff",fontSize:12,fontWeight:700,cursor:accessLevel==="demo"?"not-allowed":"pointer",opacity:accessLevel==="demo"?0.65:1}}>
              <span>🧾</span>
              <span>{accessLevel==="demo"?"Deal Worksheet — live accounts only":"Open Deal Worksheet"}</span>
            </button>
          </div>

          <div style={{flex:1,overflowY:"auto",padding:"18px 16px 8px",display:"flex",flexDirection:"column"}}>
            {msgs.map(function(m,i){
              var isA=m.role==="assistant";
              // A wizard question keeps its buttons only while it is the
              // question actually being asked. It stays in history after
              // it is answered, and its controls must go inert -- the
              // last-entry test is what does that; the other two make the
              // intent legible and cost nothing.
              var isLiveWizardQuestion=(m.kind==="wizard-question")&&i===msgs.length-1&&wizardActive&&WIZARD_STEPS[wizardStep]&&WIZARD_STEPS[wizardStep].key===m.stepKey;
              if(m.kind==="wizard-question"){
                return(
                  <div key={i} style={{marginBottom:12}}>
                    <div style={{display:"flex",justifyContent:"flex-start",marginBottom:8,gap:8,alignItems:"flex-end"}}>
                      <Av/>
                      <div style={{maxWidth:"78%",background:"linear-gradient(135deg,"+CT.navyMid+","+CT.navyLt+")",borderRadius:"4px 16px 16px 16px",padding:"10px 14px",fontSize:13,lineHeight:1.6,color:"#fff",border:"1px solid "+CT.border,whiteSpace:"pre-wrap"}}>{m.content}</div>
                    </div>
                    {isLiveWizardQuestion&&m.stepKey==="trade"&&(
                      <div style={{display:"flex",flexWrap:"wrap",gap:7,marginLeft:38}}>
                        {vehicles.map(function(v){return(
                          <button key={v.id} onClick={function(){answerWizardStep({tradeVehicleNickname:v.nickname,tradeId:v.id},"Trading in my "+v.nickname);}} style={{background:CT.card,border:"1px solid "+CT.border,borderRadius:9,padding:"8px 13px",cursor:"pointer",color:"#fff",fontSize:12,fontWeight:600}}>{v.nickname}</button>
                        );})}
                        <button onClick={function(){answerWizardStep(null,"Just adding — no trade");}} style={{background:CT.card,border:"1px solid "+CT.border,borderRadius:9,padding:"8px 13px",cursor:"pointer",color:"#fff",fontSize:12,fontWeight:600}}>Just adding — no trade</button>
                      </div>
                    )}
                    {isLiveWizardQuestion&&m.stepKey==="newUsed"&&(
                      <div style={{display:"flex",gap:7,marginLeft:38}}>
                        <button onClick={function(){answerWizardStep({isNew:true},"New");}} style={{background:CT.card,border:"1px solid "+CT.border,borderRadius:9,padding:"8px 16px",cursor:"pointer",color:"#fff",fontSize:12,fontWeight:600}}>New</button>
                        <button onClick={function(){answerWizardStep({isNew:false},"Pre-owned");}} style={{background:CT.card,border:"1px solid "+CT.border,borderRadius:9,padding:"8px 16px",cursor:"pointer",color:"#fff",fontSize:12,fontWeight:600}}>Pre-owned</button>
                      </div>
                    )}
                  </div>
                );
              }
              return(
                <div key={i} style={{display:"flex",justifyContent:isA?"flex-start":"flex-end",marginBottom:10,gap:8,alignItems:"flex-end"}}>
                  {isA&&<Av/>}
                  <div style={{maxWidth:"78%",background:isA?"linear-gradient(135deg,"+CT.navyMid+","+CT.navyLt+")":"linear-gradient(135deg,"+CT.sky+",#5A7298)",borderRadius:isA?"4px 16px 16px 16px":"16px 4px 16px 16px",padding:"10px 14px",fontSize:13,lineHeight:1.6,color:"#fff",boxShadow:"0 2px 8px rgba(0,0,0,0.25)",whiteSpace:"pre-wrap",border:isA?"1px solid "+CT.border:"none"}}>
                    {m.content}
                  </div>
                </div>
              );
            })}
            {/* Starters and the budget estimate render BELOW the
                conversation, not above it. They used to sit above a
                greeting that rendered from its own block; now the
                greeting is the first entry of msgs, so anything left
                above msgs.map appears before Kate has said anything.
                Same class of bug as the one this pass removed -- content
                positioned relative to the message list rather than
                inside it. */}
            {starters&&(
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7,marginBottom:14}}>
                {STARTERS.map(function(s){
                  // isBuying used to jump straight to onBuying() with no
                  // data at all, bypassing Kate's own intake entirely —
                  // now that she owns trade/down/budget/new-used/YMM,
                  // this starts her own wizard instead (same accelerated-
                  // on-ramp role the button always had, just pointed at
                  // the right owner).
                  return(
                    <button key={s.id} onClick={()=>s.isService?onSchedule&&onSchedule():(s.isBuying&&accessLevel!=="demo")?startWizard(s.label):send(s.label)} style={{background:CT.card,border:"1px solid "+CT.border,borderRadius:12,padding:"11px 12px",cursor:"pointer",textAlign:"left",color:"#fff",display:"flex",alignItems:"flex-start",gap:9}}>
                      <span style={{fontSize:17,lineHeight:1,flexShrink:0}}>{s.icon}</span>
                      <span style={{fontSize:12,lineHeight:1.4,color:CT.offWhite}}>{s.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {/* Kate's own guided-intake wizard — trade/down/budget/new-
                used/YMM. Tappable buttons for trade/new-used; down/budget/
                YMM answered through the normal composer below (no
                separate input needed). The rough MSRP-percentage estimate
                shows right after YMM, independent of trim — trim itself
                stays entirely Clyde's, asked only after a confirmed
                handoff (see HomeFleet_BuyingFlow.jsx's own trim-only
                wizard). */}
            {wizardAnswers.make&&wizardAnswers.model&&(function(){
              const estBase=wizardAnswers.budgetType==="monthly"&&wizardAnswers.budget
                ?solveLoan({monthlyPayment:wizardAnswers.budget,downPayment:wizardAnswers.downPayment||0,annualRatePct:FALLBACK_BASE_RATE,termMonths:72}).price
                :wizardAnswers.budget;
              if(!estBase)return null;
              return(
                <div style={{marginLeft:38,marginBottom:12,padding:"11px 13px",background:"rgba(255,255,255,0.04)",border:"1px solid "+CT.border,borderRadius:10,maxWidth:"78%"}}>
                  <div style={{fontSize:10,color:CT.muted,textTransform:"uppercase",letterSpacing:".05em",marginBottom:6}}>Rough estimate from your budget — not model-specific</div>
                  {[["Base trim",0.92],["Mid trim",1.0],["Top trim",1.12]].map(function(t){return(
                    <div key={t[0]} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#fff",marginBottom:4}}><span>{t[0]}</span><span className="mono">${Math.round(estBase*t[1]).toLocaleString()}</span></div>
                  );})}
                  <div style={{fontSize:10,color:CT.muted,marginTop:6}}>Clyde will dig into the exact trim once you hand this off to him.</div>
                </div>
              );
            })()}
            {busy&&(
              <div style={{display:"flex",alignItems:"flex-end",gap:8,marginBottom:10}}>
                <Av/>
                <div style={{background:"linear-gradient(135deg,"+CT.navyMid+","+CT.navyLt+")",borderRadius:"4px 16px 16px 16px",border:"1px solid "+CT.border,padding:"12px 16px",display:"flex",gap:5}}>
                  <Dot i={0}/><Dot i={1}/><Dot i={2}/>
                </div>
              </div>
            )}
            {capNote&&<div style={{fontSize:12,color:CT.offWhite,background:"rgba(74,158,204,0.12)",border:"1px solid rgba(74,158,204,0.3)",borderRadius:8,padding:"9px 12px",marginBottom:8,lineHeight:1.5}}>You've hit today's chat limit on the demo plan. Live accounts get more room to talk this through. {goLiveSent?<span style={{color:CT.sky,fontWeight:700}}>Request sent ✓</span>:<button onClick={()=>requestFullAccess().then(setGoLiveSent)} style={{background:"none",border:"none",padding:0,color:CT.sky,fontWeight:700,textDecoration:"none",cursor:"pointer",font:"inherit"}}>Go live →</button>}</div>}
            {err&&<div style={{fontSize:12,color:"#f87171",background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:8,padding:"8px 12px",marginBottom:8}}>{err}</div>}
            <div ref={bot}/>
          </div>

          <div style={{padding:"12px 16px 20px",borderTop:"1px solid "+CT.border,flexShrink:0}}>
            <div style={{display:"flex",gap:8,background:"rgba(255,255,255,0.05)",border:"1px solid "+CT.border,borderRadius:14,padding:"6px 6px 6px 14px",alignItems:"flex-end"}}>
              <textarea ref={inpRef} className="ci2" value={inp} onChange={function(e){setInp(e.target.value);}} onKeyDown={function(e){if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}} placeholder={"Ask "+persona.name+" anything..."} rows={1} style={{flex:1,background:"none",border:"none",color:"#fff",fontSize:13,resize:"none",fontFamily:"inherit",lineHeight:1.5,maxHeight:100,overflowY:"auto",padding:"6px 0",caretColor:persona.accent}} onInput={function(e){e.target.style.height="auto";e.target.style.height=Math.min(e.target.scrollHeight,100)+"px";}}/>
              <button onClick={()=>send()} disabled={!inp.trim()||busy} style={{background:inp.trim()&&!busy?persona.accent:CT.slate,border:"none",borderRadius:10,width:36,height:36,color:"#fff",cursor:inp.trim()&&!busy?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>
                {"➤"}
              </button>
            </div>
            <div style={{fontSize:10,color:CT.slate,textAlign:"center",marginTop:7}}>{persona.name} uses AI · Estimates only · Verify major decisions</div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}
